import { useMemo, useRef, useState } from "react";
import { ApiError, type FirstRunRequirement } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";

/**
 * What somebody sees when a dial has been set up to ask for something before it opens (KEY-434) — an
 * emergency contact, a site address, whoever is responsible today.
 *
 * <b>This screen explains the refusal; it is not the refusal.</b> The server holds the dial's detail
 * behind a 428 whatever this component does, exactly as the trial screen sits behind its 402. So this is
 * written to be the thing that makes the stop make sense: the definition's own words ("we need somebody we
 * can reach in an emergency") are shown above the field rather than under it, because they carry the
 * reason.
 *
 * <b>Every field is also marked as required, and Save is never greyed out (KEY-741).</b> The guidance
 * alone was not enough: people left fields empty and sat wondering why the form wouldn't save, because a
 * disabled button says nothing about why. So each field carries a red asterisk and a "Required" tag, and
 * pressing Save with something missing does not call the server — it marks each empty field, names them
 * beside the button, and puts focus on the first one. The form is `noValidate` so that this, not the
 * browser's own bubble, is what people see; the inputs keep `required` for assistive tech.
 *
 * The fields save through the ordinary attribute endpoint, which is deliberately ungated — if the gate
 * covered the write too it would refuse the very values it exists to collect, and nobody could ever open
 * the dial.
 *
 * A person who cannot fill a field in is told so plainly rather than shown an input that will be refused.
 * That case is real: an attribute can be restricted to administrators, and someone with a Reader's grant
 * would otherwise sit in front of a form that rejects them with no explanation.
 */
export function FirstRunCapture({
  instanceId,
  dialName,
  requirements,
  onDone,
  onBack,
}: {
  instanceId: string;
  dialName?: string | null;
  requirements: FirstRunRequirement[];
  /** Re-run the dial load. The server decides whether that now succeeds — this never assumes it will. */
  onDone: () => void;
  onBack: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set by the first Save press. The "missing" marks are derived from the values rather than stored, so a
  // field's error clears the moment it is filled in — nobody has to press Save again to find out.
  const [attempted, setAttempted] = useState(false);
  const controls = useRef(new Map<string, HTMLInputElement | HTMLSelectElement>());

  const mine = useMemo(() => requirements.filter((r) => r.canSupply), [requirements]);
  const theirs = useMemo(() => requirements.filter((r) => !r.canSupply), [requirements]);
  // A closed list sitting on "Choose one…" has the value "", so it counts as missing like an empty box.
  const missing = mine.filter((r) => (values[r.attributeKey] ?? "").trim().length === 0);
  const isMissing = (key: string) => attempted && missing.some((r) => r.attributeKey === key);

  function submit() {
    if (busy) return;
    if (missing.length > 0) {
      setAttempted(true);
      // Take them to the first gap — on a phone it may well be off-screen above the button.
      const first = controls.current.get(missing[0].attributeKey);
      first?.scrollIntoView({ block: "center" });
      first?.focus({ preventScroll: true });
      return;
    }
    void save();
  }

  async function save() {
    const token = getAccessToken();
    if (!token || busy) return;
    setBusy(true);
    setError(null);
    try {
      // One at a time, in the order they are asked for: the endpoint takes a single attribute, and a
      // partial save is not a problem here — whatever lands stays landed, and the gate simply asks for
      // whatever is still missing next time.
      for (const requirement of mine) {
        await api.setNodeAttribute(token, instanceId, {
          target: "Dial",
          sectionKey: "",
          attributeKey: requirement.attributeKey,
          value: (values[requirement.attributeKey] ?? "").trim(),
        });
      }
      onDone();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 400
          ? "That doesn't look right — check the address or number and try again."
          : e instanceof Error
            ? e.message
            : "Couldn't save that. Try again.",
      );
      setBusy(false);
    }
  }

  return (
    <main className="first-run">
      <header className="first-run-header">
        <h1>Before you start</h1>
        <p className="first-run-lede">
          {dialName ? <strong>{dialName}</strong> : "This dial"}{" "}
          {requirements.length === 1 ? "needs one detail" : `needs ${requirements.length} details`} before it
          can be opened. You'll only be asked once, and you can change them afterwards.
        </p>
      </header>

      {mine.length > 0 && (
        <form
          className="first-run-fields"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <p className="first-run-required-note">
            {mine.length === 1 ? (
              "This field is required."
            ) : (
              <>
                All fields marked <span className="first-run-req">*</span> are required.
              </>
            )}
          </p>

          {mine.map((requirement) => {
            const key = requirement.attributeKey;
            const invalid = isMissing(key);
            const errId = `first-run-${key}-error`;
            // Shared by the picker and the box: the required/invalid state and the ref Save focuses.
            const common = {
              className: invalid ? "first-run-input first-run-input--invalid" : "first-run-input",
              value: values[key] ?? "",
              required: true,
              "aria-required": true,
              "aria-invalid": invalid || undefined,
              "aria-describedby": invalid ? errId : undefined,
              ref: (el: HTMLInputElement | HTMLSelectElement | null) => {
                if (el) controls.current.set(key, el);
                else controls.current.delete(key);
              },
            } as const;
            return (
              <label key={key} className="first-run-field">
                <span className="first-run-label">
                  {requirement.label}
                  <span className="first-run-req" aria-hidden="true"> *</span>{" "}
                  <span className="first-run-req-text">Required</span>
                </span>
                {requirement.guidance && <span className="first-run-guidance">{requirement.guidance}</span>}
                {/* KEY-481: a List requirement gets the same picker the dial itself would draw. A text box
                    here would take an answer the write path then refuses — and this is the one screen where
                    being refused leaves somebody at the door with nothing to try. An OPEN list keeps the box:
                    any answer is accepted, so there is nothing a picker would add. */}
                {requirement.kind === "List" && !requirement.allowUserValues ? (
                  <select
                    {...common}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  >
                    <option value="">Choose one…</option>
                    {(requirement.options ?? []).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...common}
                    type={inputType(requirement.kind)}
                    inputMode={requirement.kind === "Phone" ? "tel" : undefined}
                    autoComplete={autoComplete(requirement.kind)}
                    list={requirement.kind === "List" ? `first-run-${key}` : undefined}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                )}
                {/* An open list still offers the author's answers — as suggestions beside a box that takes
                    anything, which is exactly what "people can add their own" means. */}
                {requirement.kind === "List" && requirement.allowUserValues && (
                  <datalist id={`first-run-${key}`}>
                    {(requirement.options ?? []).map((option) => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                )}
                {invalid && (
                  <span id={errId} className="first-run-field-error">
                    This is required
                  </span>
                )}
              </label>
            );
          })}

          {attempted && missing.length > 0 && (
            <p className="first-run-error" role="alert">
              Fill in {listOf(missing.map((r) => r.label))} to open the dial.
            </p>
          )}
          {error && <p className="first-run-error">{error}</p>}

          {/* Never disabled for being incomplete (KEY-741): a greyed-out button can't say what it's
              waiting for, and pressing this one is how somebody finds out. Busy is the only lock. */}
          <button className="first-run-go" type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save and open the dial"}
          </button>
        </form>
      )}

      {theirs.length > 0 && (
        <section className="first-run-blocked">
          <h2>{mine.length > 0 ? "And one for somebody else" : "This one isn't yours to fill in"}</h2>
          <p>
            {theirs.length === 1
              ? `“${theirs[0].label}” can only be set by a dial administrator.`
              : `${theirs.length} of these can only be set by a dial administrator.`}{" "}
            Ask whoever set this dial up, and it will open as soon as they have.
          </p>
        </section>
      )}

      <button className="first-run-back" type="button" onClick={onBack}>
        Back to my dials
      </button>
    </main>
  );
}

/** "A", "A and B", "A, B and C" — the summary reads as a sentence, not a list dump. */
function listOf(names: string[]): string {
  const quoted = names.map((n) => `“${n}”`);
  if (quoted.length <= 1) return quoted.join("");
  return `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}`;
}

/** Contact kinds get the right keyboard and the browser's own autofill; everything else is plain text. */
function inputType(kind: FirstRunRequirement["kind"]): string {
  if (kind === "Email") return "email";
  if (kind === "Phone") return "tel";
  if (kind === "Date") return "date";
  return "text";
}

function autoComplete(kind: FirstRunRequirement["kind"]): string | undefined {
  if (kind === "Email") return "email";
  if (kind === "Phone") return "tel";
  return undefined;
}
